import { useEffect, useState, useRef } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, AlertTriangle, Loader2, ArrowRight } from "lucide-react";
import Header from "./Header";
import Footer from "./Footer";
import BackToTop from "./BackToTop";
import { api, formatApiErrorDetail } from "../lib/api";
import { useCart } from "../context/CartContext";

const MAX_ATTEMPTS = 20;
const POLL_INTERVAL = 2000;

export const CheckoutSuccessPage = () => {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("pending"); // pending | paid | expired | error | timeout
  const [error, setError] = useState("");
  const [data, setData] = useState(null);
  const { clear } = useCart();
  const attemptsRef = useRef(0);
  const cartClearedRef = useRef(false);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      setError("Missing session_id.");
      return;
    }
    let cancelled = false;
    const poll = async () => {
      if (cancelled) return;
      try {
        const { data: payload } = await api.get(`/checkout/status/${sessionId}`);
        if (cancelled) return;
        setData(payload);
        if (payload.payment_status === "paid") {
          setStatus("paid");
          if (!cartClearedRef.current) {
            clear();
            cartClearedRef.current = true;
          }
          return;
        }
        if (payload.status === "expired") {
          setStatus("expired");
          return;
        }
        attemptsRef.current += 1;
        if (attemptsRef.current >= MAX_ATTEMPTS) {
          setStatus("timeout");
          return;
        }
        setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        setStatus("error");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, clear]);

  return (
    <div className="App font-sans bg-white text-brand-charcoal antialiased">
      <Header />
      <main>
        <section className="pt-28 sm:pt-32 pb-24 bg-brand-cream min-h-[60vh] flex items-center">
          <div className="max-w-2xl mx-auto px-5 sm:px-6 lg:px-10 text-center w-full">
            {status === "pending" && (
              <div data-testid="checkout-success-pending">
                <Loader2 className="h-12 w-12 text-brand-teal mx-auto animate-spin" />
                <h1 className="mt-6 font-display font-black text-3xl sm:text-4xl tracking-tight text-brand-charcoal">
                  Confirming your payment…
                </h1>
                <p className="mt-3 text-[14.5px] text-brand-charcoal/70">
                  This usually takes just a few seconds.
                </p>
              </div>
            )}
            {status === "paid" && (
              <div data-testid="checkout-success-paid">
                <span className="inline-flex h-16 w-16 items-center justify-center rounded-full bg-brand-green/15 text-brand-green">
                  <CheckCircle2 className="h-8 w-8" />
                </span>
                <h1 className="mt-6 font-display font-black text-3xl sm:text-4xl tracking-tight text-brand-charcoal">
                  Order Confirmed.
                </h1>
                <p className="mt-3 text-[15px] text-brand-charcoal/70">
                  Thanks for choosing Fresh Fast Foods. Our team will reach out shortly to confirm timing and details.
                </p>
                {data?.amount_total != null && (
                  <p className="mt-4 text-[12px] font-bold uppercase tracking-[0.18em] text-brand-charcoal/55">
                    Paid: ${(data.amount_total / 100).toFixed(2)} {data.currency?.toUpperCase()}
                  </p>
                )}
                <Link to="/" className="mt-7 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal">
                  Back to Home <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            )}
            {status === "expired" && (
              <div data-testid="checkout-success-expired">
                <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
                <h1 className="mt-5 font-display font-black text-3xl tracking-tight text-brand-charcoal">
                  Session Expired
                </h1>
                <p className="mt-3 text-[14.5px] text-brand-charcoal/70">
                  Your payment session expired before completing. You can re-add items to your cart and try again.
                </p>
                <Link to="/cart" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal">
                  Return to Cart
                </Link>
              </div>
            )}
            {status === "timeout" && (
              <div data-testid="checkout-success-timeout">
                <AlertTriangle className="h-10 w-10 text-amber-500 mx-auto" />
                <h1 className="mt-5 font-display font-black text-3xl tracking-tight text-brand-charcoal">
                  Still Processing…
                </h1>
                <p className="mt-3 text-[14.5px] text-brand-charcoal/70">
                  We&rsquo;re still confirming with Stripe. Check your email for confirmation, or contact us.
                </p>
                <Link to="/" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal">
                  Back to Home
                </Link>
              </div>
            )}
            {status === "error" && (
              <div data-testid="checkout-success-error">
                <AlertTriangle className="h-10 w-10 text-red-500 mx-auto" />
                <h1 className="mt-5 font-display font-black text-3xl tracking-tight text-brand-charcoal">
                  Something went wrong.
                </h1>
                <p className="mt-3 text-[14.5px] text-brand-charcoal/70">{error || "Unable to confirm payment."}</p>
                <Link to="/cart" className="mt-6 inline-flex items-center gap-2 rounded-full bg-brand-charcoal text-white px-5 py-3 text-[12px] font-bold uppercase tracking-[0.12em] hover:bg-brand-teal">
                  Return to Cart
                </Link>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
      <BackToTop />
    </div>
  );
};

export default CheckoutSuccessPage;
